import { useCallback, useEffect, useState } from 'react'
import type { CombatDTO, EnteteDTO, PersonnageDTO, SurvenueDTO } from '@whatremains/shared'
import { api, type Resume } from './api'
import { Barre, type Onglet } from './composants/Barre'
import { Authentification } from './ecrans/Authentification'
import { Creation } from './ecrans/Creation'
import { Fiche } from './ecrans/Fiche'
import { Carte } from './ecrans/Carte'
import { Renom } from './ecrans/Renom'
import { Quetes } from './ecrans/Quetes'
import { Survenue } from './composants/Survenue'
import { EntreeCombat } from './composants/EntreeCombat'
import { Inventaire } from './ecrans/Inventaire'
import { Negoce } from './ecrans/Negoce'
import { Donjons } from './ecrans/Donjons'
import { Confrerie } from './ecrans/Confrerie'
import { Gens } from './ecrans/Gens'
import { Besognes } from './ecrans/Besognes'
import { Engager } from './ecrans/Engager'
import { Combat } from './ecrans/Combat'

type Etat =
  | { ou: 'chargement' }
  | { ou: 'dehors' }
  | { ou: 'roster'; resume: Resume }
  | { ou: 'creation' }
  | { ou: 'jeu'; id: string; onglet: Onglet }
  /*
   * `cible` et `retour` servent le combat lance depuis une quete.
   *
   * Enzo : « aller le chercher, le battre, le rechercher, le rebattre, puis
   * retourner dans les quetes, ca n'a pas de sens ». `cible` saute la liste des
   * quarante-quatre adversaires, `retour` ramene a l'onglet d'ou l'on vient au
   * lieu de retomber sur la fiche de personnage.
   */
  | { ou: 'engager'; id: string; personnage: PersonnageDTO; cible?: string; retour?: Onglet }
  | { ou: 'combat'; id: string; combat: CombatDTO; retour?: Onglet }

export function App() {
  const [etat, setEtat] = useState<Etat>({ ou: 'chargement' })
  const [entete, setEntete] = useState<EnteteDTO | null>(null)

  const idCourant =
    etat.ou === 'jeu' || etat.ou === 'engager' || etat.ou === 'combat' ? etat.id : null

  /** La barre reflète l'état réel, donc elle se relit après tout ce qui le change. */
  // Ce qui vient d'arriver, montré sur le moment plutôt que rangé en gris dans
  // une liste qu'il faut aller ouvrir.
  const [survenue, setSurvenue] = useState<SurvenueDTO | null>(null)
  const [titresGagnes, setTitresGagnes] = useState<string[]>([])

  const rafraichirEntete = useCallback(async () => {
    if (!idCourant) return
    try {
      const e = await api.entete(idCourant)
      setEntete(e)

      /*
       * Un nom qu'on vient de gagner se dit tout de suite.
       *
       * L'en-tête est la porte où les paliers se constatent, donc c'est lui qui
       * ramène ce qui vient d'être décroché. Un titre gagné sans que personne ne
       * le dise ne serait pas très différent d'un titre jamais gagné — et il
       * resterait invisible jusqu'à ce qu'on pense à ouvrir sa fiche, ce que
       * justement on ne fait pas quand on ne sait pas qu'il y a quelque chose à
       * y voir.
       */
      if (e.titresGagnes.length > 0) {
        setTitresGagnes((f) => [...f, ...e.titresGagnes.map((t) => t.nom)])
      }
      /*
       * Ce qui est arrivé à l'artefact pendant qu'on ne regardait pas.
       *
       * Une jauge peut se terminer dans une transaction déclenchée par autre
       * chose, ou pendant une absence : l'Arrêt de Perennis monte tout seul, la
       * Dérive de Pharos monte en relevant une besogne. On le dit au premier
       * écran qui revient, où qu'il soit, et on l'acquitte après l'avoir
       * affiché — pas avant, sinon on le perdrait précisément là où l'on tenait
       * à le dire.
       */
      if (e.survenueArtefact) {
        setSurvenue({
          titre: 'Il n’est plus là',
          ton: 'perte',
          texte: e.survenueArtefact.texte,
          lignes: [],
        })
        await api.survenueArtefactVue(idCourant).catch(() => null)
      }
    } catch {
      setEntete(null)
    }
  }, [idCourant])

  useEffect(() => { void rafraichirEntete() }, [rafraichirEntete, etat])

  const reprendre = useCallback(async () => {
    try {
      const resume = await api.moi()
      if (resume.personnages.length === 0) return setEtat({ ou: 'creation' })

      const seul = resume.personnages.length === 1 ? resume.personnages[0] : null
      if (!seul) return setEtat({ ou: 'roster', resume })

      // Rien ne se perd : un combat interrompu se rouvre sur son tour.
      try {
        const combat = await api.combatEnCours(seul.id)
        if (!combat.fini && combat.interactif) {
          return setEtat({ ou: 'combat', id: seul.id, combat })
        }
      } catch {
        // Aucun combat en cours, cas normal.
      }
      setEtat({ ou: 'jeu', id: seul.id, onglet: 'personnage' })
    } catch {
      setEtat({ ou: 'dehors' })
    }
  }, [])

  useEffect(() => { void reprendre() }, [reprendre])

  /**
   * Reprendre un combat en cours, et savoir d'où l'on vient.
   *
   * Enzo : « quand on finit un combat de donjon, le bouton revenir devrait nous
   * ramener dans le donjon à l'étape suivante, pas sur l'onglet personnage. »
   *
   * `retour` n'était posé que pour les combats lancés depuis une quête ; un
   * combat de donjon le laissait vide et retombait sur `personnage`. On sortait
   * donc du donjon à chaque rencontre, et il fallait y revenir à la main —
   * exactement le va-et-vient que ce champ existe pour supprimer.
   */
  async function rouvrirCombat(id: string, retour?: Onglet) {
    try {
      setEtat({ ou: 'combat', id, combat: await api.combatEnCours(id), retour })
    } catch {
      void rafraichirEntete()
    }
  }

  function corps() {
    switch (etat.ou) {
      case 'chargement':
        return <div className="cadre"><div className="entete"><p className="tenu">…</p></div></div>

      case 'dehors':
        return <Authentification surEntree={() => void reprendre()} />

      case 'creation':
        return <Creation surCree={(id) => setEtat({ ou: 'jeu', id, onglet: 'personnage' })} />

      case 'jeu':
        switch (etat.onglet) {
          case 'carte':
            return <Carte personnageId={etat.id} surChangement={() => void rafraichirEntete()} />
          case 'renom':
            return <Renom personnageId={etat.id} />
          case 'quetes':
            return (
              <Quetes
                personnageId={etat.id}
                surChangement={() => void rafraichirEntete()}
                surSurvenue={setSurvenue}
                surCarte={() => setEtat({ ou: 'jeu', id: etat.id, onglet: 'carte' })}
                /*
                  Le combat demandé par une étape, lancé depuis la quête.
                  On charge le personnage ici parce que l'écran d'engagement en
                  a besoin pour dire où l'on se situe face à l'adversaire.
                */
                surCombat={(adversaire) =>
                  void api.personnage(etat.id).then((p) =>
                    setEtat({
                      ou: 'engager', id: etat.id, personnage: p,
                      cible: adversaire, retour: 'quetes',
                    }),
                  )
                }
              />
            )
          case 'combat':
            /*
             * Se battre a son onglet.
             *
             * L'écran d'engagement existait déjà, mais on n'y entrait que par
             * un bouton au bas de la fiche de personnage. On le rend joignable
             * directement, sans changer l'écran lui-même : c'est un chemin
             * d'accès qu'on ajoute, pas une fonctionnalité qu'on réécrit.
             *
             * `retour` vaut ici l'onglet lui-même, et c'est ce qui compte :
             * sortir d'un combat lancé depuis cet onglet doit y ramener, pas
             * renvoyer sur la fiche comme le faisait l'ancien chemin.
             */
            return (
              <EntreeCombat
                id={etat.id}
                surEngage={(c) =>
                  setEtat({ ou: 'combat', id: etat.id, combat: c, retour: 'combat' })
                }
              />
            )
          case 'besognes':
            return (
              <Besognes personnageId={etat.id} surChangement={() => void rafraichirEntete()} />
            )
          case 'inventaire':
            return <Inventaire id={etat.id} surChangement={() => void rafraichirEntete()} />
          case 'negoce':
            return <Negoce id={etat.id} surChangement={() => void rafraichirEntete()} />
          case 'gens':
            return <Gens id={etat.id} surChangement={() => void rafraichirEntete()} />
          case 'maison':
            return <Confrerie id={etat.id} surChangement={() => void rafraichirEntete()} />
          case 'donjons':
            return (
              <Donjons
                id={etat.id}
                surChangement={() => void rafraichirEntete()}
                surCombat={() => void rouvrirCombat(etat.id, 'donjons')}
              />
            )
          case 'personnage':
            return (
              <Fiche
                id={etat.id}
                surChangement={() => void rafraichirEntete()}
                surSortie={() => void api.deconnexion().then(() => setEtat({ ou: 'dehors' }))}
              />
            )
        }
        break

      case 'engager':
        return (
          <Engager
            personnage={etat.personnage}
            cibleImposee={etat.cible}
            surEngage={(c) =>
              setEtat({ ou: 'combat', id: etat.id, combat: c, retour: etat.retour })
            }
            surSortie={() =>
              setEtat({ ou: 'jeu', id: etat.id, onglet: etat.retour ?? 'personnage' })
            }
          />
        )

      case 'combat':
        return (
          <Combat
            personnageId={etat.id}
            combatInitial={etat.combat}
            surChangement={() => void rafraichirEntete()}
            // On revient là d'où l'on vient. Un combat de quête ramène à la
            // quête, ce qui est le point : c'est le va-et-vient qui fatiguait.
            surSortie={() =>
              setEtat({ ou: 'jeu', id: etat.id, onglet: etat.retour ?? 'personnage' })
            }
          />
        )

      case 'roster':
        return (
          <div className="cadre">
            <div className="entete">
              <div className="surtitre">{etat.resume.compte.email}</div>
              <h1>Qui</h1>
            </div>
            <section>
              <div className="grille">
                {etat.resume.personnages.map((p) => (
                  <button
                    key={p.id}
                    className="choix"
                    onClick={() => setEtat({ ou: 'jeu', id: p.id, onglet: 'personnage' })}
                  >
                    <span className="role">Niveau {p.niveau} · {p.classe}</span>
                    <span className="nom">{p.nom}</span>
                  </button>
                ))}
              </div>
              <p style={{ marginTop: 22 }}>
                <button className="discret" onClick={() => setEtat({ ou: 'creation' })}>
                  Quelqu'un d'autre
                </button>
              </p>
            </section>
          </div>
        )
    }
    return null
  }

  return (
    <>
      <Barre
        surQuitter={() => void api.deconnexion().then(() => setEtat({ ou: 'dehors' }))}
        entete={idCourant ? entete : null}
        actif={etat.ou === 'jeu' ? etat.onglet : null}
        surOnglet={(o) => idCourant && setEtat({ ou: 'jeu', id: idCourant, onglet: o })}
        surCombat={() => idCourant && void rouvrirCombat(idCourant)}
      />
      {corps()}
      {/*
        L'annonce des titres. Un bandeau et pas une fenêtre : on vient de faire
        autre chose (finir une quête, sortir d'un donjon), et couper le geste
        par une modale pour annoncer une bonne nouvelle punit le joueur de
        l'avoir méritée.
      */}
      {titresGagnes.length > 0 && (
        <div className="annonce-titre" role="status">
          <span>
            On t’appelle désormais <b>{titresGagnes.join(', ')}</b>.
          </span>
          <button className="discret" onClick={() => setTitresGagnes([])}>
            bien
          </button>
        </div>
      )}
      <Survenue survenue={survenue} surFermer={() => setSurvenue(null)} />
    </>
  )
}
